// Cave84 — Locations service (Phase 1 Week 2)
// Pure data-access layer for the top-level `locations` collection.
// Each Location has a `cellar_id` FK; only the Home cellar has sub-locations
// (Fridge 1–6). Abroad and Virtual cellars are flat (no sub-locations).
//
// Pages/components MUST NOT import from here directly — use hooks/useLocations.ts.

import {
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import type { Location } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';

const COLLECTION = 'locations';

export type LocationCreateInput = Omit<Location, 'id' | 'current_count'>;
export type LocationUpdateInput = Partial<Omit<Location, 'id' | 'current_count'>>;

function locationsRef() {
  return typedCollection<Location>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function listLocationsByCellar(cellarId: string): Promise<Location[]> {
  const snap = await getDocs(
    query(locationsRef(), where('cellar_id', '==', cellarId), orderBy('sort_order', 'asc'))
  );
  return snap.docs.map((d) => d.data());
}

// ---------------------------------------------------------------------------
// Mutations (owner-only — guarded by Firestore rules and the hook layer)
// ---------------------------------------------------------------------------

export async function createLocation(input: LocationCreateInput): Promise<Location> {
  const payload = {
    ...input,
    current_count: 0,
    created_at: serverTimestamp()
  };
  const rawRef = await addDoc(locationsRef(), payload as unknown as Location);
  // Return an optimistic shape; the hook layer will refetch to get the real doc.
  return { id: rawRef.id, current_count: 0, ...input } as Location;
}

export async function updateLocation(id: string, patch: LocationUpdateInput): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), patch);
}

/**
 * Locations can only be deleted when current_count === 0.
 * The hook layer enforces this; this function does the raw delete.
 */
export async function deleteLocationRaw(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
